/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

// 3rd party libraries
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Col,
  Form,
  Input,
  InputRef,
  message,
  Modal,
  Row,
  // Select,
  Space,
  // Spin,
  Typography
} from 'antd';
import _debounce from 'lodash/fp/debounce';
import i18n from '@utils/i18n';
import AppConstants from '@utils/AppConstants';
import AppUtil from '@utils/AppUtil';
import { useSelector } from 'react-redux';
import { ParseFeedback, ParseResult } from '@/components/ParseFeedback';
import './NewFlow.less';
import useALC from '@/hooks/useALCFlag';
import { userSelector } from '../stores/slices/user';
import {
  CreateProcedureMutationVariables,
  Department,
  useCreateProcedureMutation,
  useCreateProcedureV2Mutation
} from '../generated/graphql';

// Component i18n
const i18nt = (s: string, d?: string) => i18n.t('NewFlow', s, d);

function template(language: string) {
  if (language === 'python') {
    return `from kognitos.bci import procedure

@procedure
def handler(brain):
  # TODO implement
  return
`;
  }

  if (language === 'nodejs') {
    return `// TODO implement
return
`;
  }

  return '';
}

// Component Prop Types
interface IProps {
  /**
   * Success Callback -- called back w/ new flow
   */
  onSuccess: (data: any) => void;
  /**
   * Close Callback
   */
  onClose: () => void;

  department: Department;

  /**
   * Procedure to be cloned
   */
  procedureToClone: {
    id?: string;
    name: string;
    title?: string;
    description: string;
    language: string;
    text: string;
  };

  procedureBody?: string;
  suffix?: string;

  title?: string;
  procedureLanguage?: string;
}

const getCloneName = (
  procedureName: string,
  suffix: string | undefined
): string => {
  if (suffix) {
    return `${procedureName.trim()}${suffix}`;
  }
  const match = procedureName.match(AppConstants.PATTERNS.CLONE_SUFFIX);
  if (!match) {
    return `${procedureName.trim()} - clone`;
  }
  const cloneSuffix = procedureName.split('-')[1].trim();
  const matchDigit = cloneSuffix.match(AppConstants.PATTERNS.NUMBERS);
  const foundNumber = matchDigit?.[0];
  const normalisedName = procedureName
    .replace(AppConstants.PATTERNS.CLONE_SUFFIX, '')
    .trim();
  if (!foundNumber) {
    return `${normalisedName} - 1 clone`;
  }
  return `${normalisedName} - ${Number(foundNumber) + 1} clone`;
};

// Component implementation
function NewFlow(props: IProps) {
  const {
    onSuccess,
    onClose,
    department,
    procedureToClone,
    procedureBody,
    title = 'New Process',
    suffix,
    procedureLanguage = 'english'
  } = props;

  // The new form instance
  const [theForm] = Form.useForm();
  const { isALCEnabled } = useALC();

  // Is the popup visible
  const [visible, setVisible] = useState(true);
  // The form's field values
  const [formState, setFormState] = useState({
    name: procedureToClone ? getCloneName(procedureToClone.name, suffix) : '',
    description: procedureToClone?.description ?? '',
    title: procedureToClone ? getCloneName(procedureToClone.name, suffix) : '',
    procedureType:
      procedureLanguage ??
      procedureToClone?.language ??
      AppConstants.PROCEDURE_TYPE_MAP.english.type,
    procedure: procedureToClone?.text ?? procedureBody ?? template('english')
  });

  // TODO: Move - added as a temp fix
  const [loading, toggleLoading] = useState(false);

  // TODO: CreateProcedureV2Mutation
  const [createDraftProcedure] = useCreateProcedureV2Mutation();

  const [createProcedure] = useCreateProcedureMutation();

  const iRef = useRef<InputRef>(null);

  useEffect(() => {
    iRef.current?.focus();
  }, []);

  const { username } = useSelector(userSelector);

  const [parseResult, setParseResult] = useState<ParseResult>(ParseResult.FAIL);
  const [isValid, setIsValid] = useState<boolean>(false);

  const formattedProcedureText = AppUtil.formatProcedureText(
    formState.procedure
  );

  const onOK = () => {
    theForm
      .validateFields(['name', 'procedure'])
      .then(() => {
        const variables: CreateProcedureMutationVariables = {
          input: {
            departmentId: department.id,
            name: formState.name.trim(),
            title: formState.title.trim(),
            text: formattedProcedureText,
            language: formState.procedureType.trim(),
            owner: username
          }
        };

        toggleLoading(true);

        const newProcessAPICall = isALCEnabled
          ? createDraftProcedure({
              variables
            })
          : createProcedure({
              variables
            });

        newProcessAPICall
          .then((resp) => {
            if (resp.data) {
              onSuccess?.(resp.data);
              onClose();
            }
          })
          .catch((e) => {
            // TODO: we need proper error type here
            const alreadyExists = (e.message || '').includes('already exists');

            message.open({
              type: 'error',
              className: alreadyExists
                ? 'procedure-already-exists-error-message'
                : '',
              content: e.message || 'Failed to create a new process',
              duration: 5
            });
          })
          .finally(() => {
            toggleLoading(false);
          });
      })
      .catch((errorInfo) => {
        AppUtil.logDebug(errorInfo);
      });
  };

  const onCancel = () => {
    setVisible(false);
    onClose();
  };

  const onChange = (key: string, value: any) => {
    const newState: any = { ...formState };
    newState[key] = value;
    if (newState.procedure === template(formState.procedureType)) {
      newState.procedure = template(newState.procedureType);
    }
    setFormState(newState);
  };

  const onSubmit = () => {
    if (parseResult !== ParseResult.PASS) {
      message.error('Please enter a valid procedure name!');
      return;
    }
    onOK();
  };

  const allowSubmit = [
    parseResult === ParseResult.PASS,
    !!formState.name.trim(),
    !!formattedProcedureText,
    isValid
  ].every(Boolean);

  return (
    <Modal
      className="new-flow-modal"
      title={
        <Row gutter={[0, 12]}>
          <Col span={24}>
            <Space>
              <Typography.Title level={5} className="title">
                {isALCEnabled ? 'Create a draft process' : title}
              </Typography.Title>
              <ParseFeedback
                statement={formState.name.trim()}
                scopeCode={formattedProcedureText || 'do nothing'}
                language={formState.procedureType.trim()}
                departmentId={department?.id}
                parseCallback={setParseResult}
              />
            </Space>
          </Col>

          {parseResult === ParseResult.FAIL && (
            <Col span={24}>
              <Alert
                type="error"
                data-cy="parse-error-alert"
                message={
                  <Row>
                    <Typography.Text>
                      The name you entered is not a valid procedure name.
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Examples of valid procedure names are:{' '}
                      {`"to do
                      something"`}
                      , {`"a book is good if"`},{' '}
                      {`"the cool
                      penguins are"`}
                      , {`"the valid string is"`}.
                    </Typography.Text>
                  </Row>
                }
              />
            </Col>
          )}
        </Row>
      }
      centered
      open={visible}
      onOk={onSubmit}
      okText={isALCEnabled ? 'Create Draft Process' : 'Ok'}
      okButtonProps={{
        // @ts-ignore
        'data-cy': 'ok-btn',
        disabled: !allowSubmit
      }}
      onCancel={onCancel}
      width={800}
      confirmLoading={loading}
      keyboard={false}
      maskClosable={false}
    >
      <Form
        form={theForm}
        layout="vertical"
        requiredMark={false}
        validateTrigger={['onBlur', 'onChange']}
      >
        <Form.Item
          name="Process name"
          data-cy="procedure-name-for-cloning"
          label={i18nt('name', 'Name')}
          help={i18nt(
            'invalid_procedure_name',
            'Please enter a name that starts with "to" or ends with "is", "if", or "are"'
          )}
          rules={[
            {
              validator: () =>
                new Promise<void>((resolve, reject) => {
                  setIsValid(false);
                  const pName = formState.name.trim().toLocaleLowerCase();
                  const procedureNameRegex = /^(to.+|.+are|.+if|.+is)$/;
                  if (!!pName && procedureNameRegex.test(pName)) {
                    setIsValid(true);
                    resolve();
                    return;
                  }
                  setIsValid(false);
                  reject();
                }),
              whitespace: true
            }
          ]}
        >
          <Input
            ref={iRef}
            data-cy="procedure-name"
            className="new-procedure-name"
            placeholder="to do something"
            autoComplete="off"
            disabled={loading}
            onChange={(e) => onChange('name', e.target.value)}
            value={formState.name}
          />
        </Form.Item>
      </Form>
      {isALCEnabled && (
        <div className="alcInfo">
          <Typography.Text>
            This playground will be saved as a draft process. You can now test
            the automation using email and other API triggers.
          </Typography.Text>
          <Typography.Text>
            Staging Credentials will be continued to be used till you take the
            process live.
          </Typography.Text>
        </div>
      )}
    </Modal>
  );
}

export default NewFlow;
